import { Component, ElementRef, Injector } from '@angular/core';
import { Validators } from '@angular/forms';
import { ContratoService, ConvenioService, DespesaService, EmpenhoService, FuncaoGovernoService, LicitacaoService, RecursoService } from 'administrativo-lib';
import { CompraService, ModalidadeService } from 'compra-lib';
import { Acao, BaseResourceFormComponent, Compra, Contrato, Convenio, DateFormatPipe, Despesa, EddyAutoComplete, EmpenhoResto, Favorecido, FavorecidoService, FuncaoGoverno, GlobalService, Licitacao, LoginContabil, Modalidade, Programa, Recurso } from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { takeUntil } from 'rxjs/operators';
import { AcaoService } from '../../planejamento/acao/service/acao.service';
import { ProgramaService } from '../../planejamento/programa/service/programa.service';
import { EmpenhoRestoService } from '../service/empenho-resto.service';

@Component({
  selector: 'app-empenho-resto-form',
  templateUrl: './empenho-resto-form.component.html'
})
export class EmpenhoRestoFormComponent extends BaseResourceFormComponent<EmpenhoResto, LoginContabil> {
  /**
   * Declaração de variáveis
   */
  public empenhado: number;
  public liquidado: number;
  public contratoAutoComplete: EddyAutoComplete<Contrato>;
  public compraAutoComplete: EddyAutoComplete<Compra>;
  public favorecidoAutoComplete: EddyAutoComplete<Favorecido>;
  public subElementoAutoComplete: EddyAutoComplete<Despesa>;
  public licitacaoAutoComplete: EddyAutoComplete<Licitacao>;
  public convenioAutoComplete: EddyAutoComplete<Convenio>;
  public programaAutoComplete: EddyAutoComplete<Programa>;
  public acaoAutoComplete: EddyAutoComplete<Acao>;
  public funcaoAutoComplete: EddyAutoComplete<FuncaoGoverno>;
  public subFuncaoAutoComplete: EddyAutoComplete<FuncaoGoverno>;
  public aplicacaoAutoComplete: EddyAutoComplete<Recurso>;
  public recursoVariavelAutoComplete: EddyAutoComplete<Recurso>;
  public pago: number;
  public listaModalidades: Array<Modalidade>;
  public saldoConvenio: number = 0;
  public saldoContrato: number = 0;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    protected globalService: GlobalService,
    protected favorecidoService: FavorecidoService,
    protected licitacaoService: LicitacaoService,
    protected contratoService: ContratoService,
    protected compraService: CompraService,
    protected convenioService: ConvenioService,
    protected despesaService: DespesaService,
    protected modalidadeService: ModalidadeService,
    private messageService: MessageService,
    private empenhoService: EmpenhoService,
    protected programaService: ProgramaService,
    protected acaoService: AcaoService,
    protected funcaoGovernoService: FuncaoGovernoService,
    protected recursoService: RecursoService,
    protected empenhoRestoService: EmpenhoRestoService) {
    super(new EmpenhoResto(), injector, EmpenhoResto.converteJson, empenhoRestoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      valor_empenho: 0,
      data_empenho: [null, [Validators.required]],
      data_vencimento: [null, [Validators.required]],
      especie: [null, [Validators.required]],
      processo: [null, [Validators.required]],
      historico: [null, [Validators.required, Validators.minLength(2)]],
      orgao: [null, [Validators.required]],
      favorecido: [null, [Validators.required]],
      subelemento: [null, [Validators.required]],
      subelemento_nome: [null, [Validators.required]],
      programa: [null, [Validators.required]],
      programa_nome: [null, [Validators.required]],
      acao: [null, [Validators.required]],
      acao_nome: [null, [Validators.required]],
      funcao: [null, [Validators.required]],
      funcao_nome: [null, [Validators.required]],
      subfuncao: [null, [Validators.required]],
      subfuncao_nome: [null, [Validators.required]],
      recurso: [null, [Validators.required]],
      recurso_nome: [null, [Validators.required]],
      aplicacao: [null, [Validators.required]],
      aplicacao_nome: [null, [Validators.required]],
      recurso_variavel: [null],
      recurso_variavel_nome: [null],
      contrato: [null],
      compra: [null],
      modalidade: [null],
      ata: [null],
      convenio: [null],
      licitacao: [null],
      documento: [null],
      mes: [null],
      exercicio: [this.login.exercicio, [Validators.required]],
      ano: [null, [Validators.required]],
      inscrito_resto: [null],
      usuario_cadastro: [this.login.usuario, [Validators.required]]
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'favorecido,compra,contrato,licitacao,modalidade,convenio,orgao' };
  }

  protected afterInit(): void {
    this.modalidadeService.filtrar(1, -1, { 'cidade.id': this.login.cidade.id, orderBy: 'codigo' }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaModalidades = res ? res.content : new Array<Modalidade>();
      }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
    this.carregarAutoCompletes();
    if (this.currentActionRoute === 'novo') {
      this.empenhoService.proximoNumeroEmpenho(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          this.entidadeForm.get('numero').setValue(res);
          this.empenhoService.ultimaDataEmpenhada(this.login.exercicio.id, this.login.orgao.id)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((dataEmp) => {
              if (dataEmp === 0) {
                this.entidade.data_empenho = new Date();
              } else {
                this.entidade.data_empenho = new DateFormatPipe().transform(dataEmp, []);
              }
              this.entidadeForm.get('data_empenho').setValue(this.entidade.data_empenho);
              this.entidadeForm.get('mes').setValue(this.entidade.data_empenho.getUTCMonth() + 1);
            });
        });
    }
  }

  protected campoFoco(): ElementRef {
    return null;
  }

  protected beforeSubmit() {
    try {
      if (!this.entidadeForm.get('numero').value) {
        throw new Error('Informe o número do empenho');
      }
      if (!this.entidadeForm.get('exercicio').value) {
        throw new Error('Informe o exercício do empenho');
      }
      if (!this.entidadeForm.get('orgao').value) {
        throw new Error('Informe o órgão do empenho');
      }
      if (!this.entidadeForm.get('subelemento').value) {
        throw new Error('Informe a despesa do empenho');
      }
      if (!this.entidadeForm.get('subelemento_nome').value) {
        throw new Error('Informe o nome da despesa do empenho');
      }
      if (!this.entidadeForm.get('favorecido').value) {
        throw new Error('Informe o favorecido do empenho');
      }
      if (!this.entidadeForm.get('historico').value) {
        throw new Error('Informe o histórico do empenho');
      }
      if (!this.entidadeForm.get('valor_empenho').value) {
        throw new Error('Informe o valor do empenho');
      }
      // if (this.entidadeForm.get('contrato').value && this.saldoContrato <= 0) {
      //   throw new Error('Não há saldo no contrato para empenhar, veja se existe aditivos!');
      // }
      if (this.entidadeForm.get('convenio').value && this.saldoConvenio <= 0) {
        throw new Error('Não há saldo no convênio para empenhar!');
      }
      if (this.entidadeForm.get('valor_empenho').value <= 0) {
        this.entidadeForm.get('valor_empenho').setValue(this.entidadeForm.get('valor_empenho').value * -1);
      }

      if (this.entidadeForm.get('licitacao').value === '') {
        this.entidadeForm.get('licitacao').setValue(null);
      }
      if (this.entidadeForm.get('convenio').value === '') {
        this.entidadeForm.get('convenio').setValue(null);
      }
      const dtEmpenho: Date = this.entidadeForm.get('data_empenho').value;
      if (dtEmpenho.getFullYear() !== this.entidadeForm.get('ano').value) {
        throw new Error('O ano da data do empenho está diferente do exercício logado');
      }
      if (!this.entidadeForm.get('mes').value)
        this.entidadeForm.get('mes').setValue(dtEmpenho.getUTCMonth() + 1);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: EmpenhoResto) {
    this.mensagemSucesso = `Registro salvo com sucesso!\nEmpenho nº ${ent.numero}`;
    if (this.currentActionRoute === 'novo' && this.login.redirecionar_liquidar_pagar
      && this.podeIncluir('/liquidacoes')) {
      this.router.navigate(['/liquidacoes', 'novo', ent.id]);
    } else if (this.currentActionRoute === 'novo' && !this.limparTela) {
      this.router.navigate(['/empenhos', ent.id, 'editar']);
    }
  }

  protected async afterLoad() {

    this.entidade.data_empenho = new DateFormatPipe().transform(this.entidade.data_empenho, []);
    this.entidade.data_vencimento = new DateFormatPipe().transform(this.entidade.data_vencimento, []);
    this.contratoAutoComplete.id = this.entidade.contrato ? this.entidade.contrato.id : null;
    this.compraAutoComplete.id = this.entidade.compra ? this.entidade.compra.id : null;
    this.favorecidoAutoComplete.id = this.entidade.favorecido ? this.entidade.favorecido.id : null;
    this.subElementoAutoComplete.id = this.entidade.subelemento_nome;
    this.programaAutoComplete.id = this.entidade.programa_nome;
    this.acaoAutoComplete.id = this.entidade.acao_nome;
    this.funcaoAutoComplete.id = this.entidade.funcao_nome;
    this.subFuncaoAutoComplete.id = this.entidade.subfuncao_nome;
    this.aplicacaoAutoComplete.id = this.entidade.aplicacao_nome;
    this.recursoVariavelAutoComplete.id = this.entidade.recurso_variavel_nome;
    this.licitacaoAutoComplete.id = this.entidade.licitacao ? this.entidade.licitacao.id : null;
    this.convenioAutoComplete.id = this.entidade.convenio ? this.entidade.convenio.id : null;

    this.atualizarCompraAutoComplete();
    if (this.entidade.contrato) {
      this.obterSaldoContrato(this.entidade.contrato);
    }

    await this.loadTotalizadores();
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private async loadTotalizadores() {
    this.empenhado = 0;
    this.liquidado = 0;
    this.pago = 0;

    let totalizadores = await this.empenhoRestoService.totalizadores(
      this.entidade.ano, this.login.orgao.id, this.entidade.id).toPromise();

    if (totalizadores) {
      totalizadores = totalizadores.content[0] ? totalizadores.content[0] : totalizadores.content;
      this.empenhado = totalizadores.total_empenhado;
      this.liquidado = totalizadores.total_liquidado;
      this.pago = totalizadores.total_pago;
    }
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
      case 'controle-interno':
        this.router.navigate(['/contabilidade']);
        break;
      default:
        this.sair();
        break;
    }
  }

  public conversor = (entidade: object | string) => {
    if (typeof entidade === 'string') {
      return entidade;
    }
    return entidade ? entidade['nome'] : null;
  }

  public atualizarCompraAutoComplete() {
    this.compraAutoComplete = new EddyAutoComplete(this.entidadeForm.get('compra'), this.compraService, 'numero', ['numero'],
      { 'exercicio.ano': this.entidadeForm.get('ano').value ? this.entidadeForm.get('ano').value : this.entidade.ano, orgao_id: this.login.orgao.id, relations: 'exercicio', orderBy: 'numero', ignoreCondObrig: '' },
      { number: ['id', 'numero'] }
    );
  }

  public obterSaldoConvenio() {
    this.empenhoService.totalEmpenhadoPorContrato(this.entidadeForm.get('convenio').value.id, this.login.orgao.id)
      .subscribe((saldo) => {
        this.saldoConvenio = this.entidadeForm.get('convenio').value.valor_convenio - saldo;
      });
  }

  public obterSaldoContrato(contrato: Contrato) {
    this.empenhoService.totalEmpenhadoPorContrato(contrato ? contrato.id : this.entidadeForm.get('contrato').value.id, this.login.orgao.id)
      .subscribe((saldo) => {
        if (contrato.tipo_contratacao && contrato.tipo_contratacao.material && contrato.tipo_contratacao.servico) {
          if (this.entidadeForm.get('subelemento').value.substr(0, 6) == '449052') {
            this.saldoContrato = this.entidadeForm.get('contrato').value.valor_material - saldo;
          } else if (this.entidadeForm.get('ficha').value && (
            this.entidadeForm.get('subelemento').value.substr(0, 6) == '339036' || this.entidadeForm.get('subelemento').value.substr(0, 6) == '339039')) {
            this.saldoContrato = this.entidadeForm.get('contrato').value.valor_servico - saldo;
          } else {
            this.saldoContrato = this.entidadeForm.get('contrato').value.valor_contrato - saldo;
          }
        } else {
          this.saldoContrato = this.entidadeForm.get('contrato').value.valor_contrato - saldo;
        }
      });
  }

  private carregarAutoCompletes() {
    this.subElementoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('subelemento'), this.despesaService, 'nome', ['nome'],
      Object.assign({}, { exercicio_id: this.login.exercicio.id, nivel: 6, orderBy: 'codigo', }),
      { number: ['id', 'codigo'], text: ['nome'] },
      () => {
      }
    );

    this.favorecidoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('favorecido'), this.favorecidoService,
      'id', ['nome'], { cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['id', 'cpf_cnpj'], text: ['nome'] },
      () => {
        // this.preencherDadosUltimoEmpenho();
      }
    );

    this.compraAutoComplete = new EddyAutoComplete(this.entidadeForm.get('compra'), this.compraService, 'numero', ['numero'],
      { 'exercicio.ano': this.entidadeForm.get('ano').value, orgao_id: this.login.orgao.id, relations: 'exercicio', orderBy: 'numero', ignoreCondObrig: '' },
      { number: ['id', 'numero'] }
    );

    // autocomplete para licitacao
    this.licitacaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('licitacao'), this.licitacaoService,
      'numero', ['numero'], { orgao_id: this.login.orgao.id, orderBy: 'numero' }, { number: ['numero'] }
    );

    // autocomplete para convenio
    this.convenioAutoComplete = new EddyAutoComplete(this.entidadeForm.get('convenio'), this.convenioService,
      'numero', ['numero', 'ano'], { orgao_id: this.login.orgao.id, orderBy: 'numero' }, { number: ['id', 'numero'] }
    );
    
    // autocomplete para programa
    this.programaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('programa'), this.programaService,
      'codigo', ['nome'], { ppa_id: this.login.ppa.id, orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] }
    );

    // autocomplete para acao
    this.acaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('acao'), this.acaoService,
      'codigo', ['nome'], { ppa_id: this.login.ppa.id, orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] }
    );
    
    // autocomplete para funcao governo
    this.funcaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('funcao'), this.funcaoGovernoService,
      'codigo', ['nome'], { nivel: 0, cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] }
    );

    // autocomplete para subfuncao governo
    this.subFuncaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('subfuncao'), this.funcaoGovernoService,
      'codigo', ['nome'], { nivel: 1, cidade_id: this.login.cidade.id, orderBy: 'nome' }, { number: ['codigo'], text: ['nome'] }
    );
    
    // autocomplete para aplicacao
    this.aplicacaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('aplicacao'),
      this.recursoService, 'codigo', ['nome'],
      { cidade_id: this.login.cidade.id, nivel$in: '1,2', orderBy: 'codigo' },
      { number: ['codigo'], text: ['nome'] }
    );
    
    // autocomplete para aplicacao
    this.recursoVariavelAutoComplete = new EddyAutoComplete(this.entidadeForm.get('recurso_variavel'),
      this.recursoService, 'codigo', ['nome'],
      { cidade_id: this.login.cidade.id, nivel$in: '3', orderBy: 'codigo' },
      { number: ['codigo'], text: ['nome'] }
    );

    this.contratoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('contrato'), this.contratoService,
      'numero', ['numero', 'aditivo'], {
      orgao_id: this.login.orgao.id, orderBy: 'numero',
      relations: 'licitacao,tipo_contratacao'
    }, { text: ['numero'] },
      () => {
        const contrato: Contrato = this.entidadeForm.get('contrato').value
          ? this.entidadeForm.get('contrato').value
          : this.entidade.contrato;
        if (contrato) {
          this.entidadeForm.get('licitacao').setValue(null);
          this.entidadeForm.get('modalidade').setValue(null);
          this.entidadeForm.get('contrato').setValue(contrato);
          this.entidadeForm.get('processo').setValue(contrato.processo);
          if (contrato.licitacao) {
            this.licitacaoService.obter({ id: contrato.licitacao.id, relations: 'modalidade' }).pipe(takeUntil(this.unsubscribe))
              .subscribe((res) => {
                this.entidadeForm.get('licitacao').setValue(res);
                this.entidadeForm.get('modalidade').setValue(res.modalidade);
              }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
          }
          this.obterSaldoContrato(contrato);
        }
      }
    );
  }
}
